import type { LogisticsRow } from '@contracts';

/**
 * Tabla de logística. Muestra UNIDADES enteras (stock, a resurtir, días), no
 * dinero — a diferencia de TableWidget que formatea todo como MXN. Las columnas
 * se muestran según los campos presentes en cada fila.
 */
export function LogisticsTableWidget({ data }: { data: LogisticsRow[] }) {
  const rows = Array.isArray(data) ? data : [];
  if (!rows.length) {
    return <div className="grid h-full place-items-center text-sm text-slate-400">Sin datos</div>;
  }

  // Detectar qué columnas mostrar según los campos presentes.
  const has = (k: keyof LogisticsRow) => rows.some((r) => r[k] !== undefined && r[k] !== null);
  const showStock = has('stock');
  const showForecast = has('forecast');
  const showUnits = has('units');
  const showMin = has('min');
  const showMax = has('max');
  const showDays = has('days');
  const showFrom = has('from');
  const showPos = has('pos');
  const showSupplier = has('supplier');
  const showEstCost = has('estCost');

  const dot = (s?: string) =>
    s === 'critical' ? 'bg-red-500' : s === 'warn' ? 'bg-amber-400' : s === 'ok' ? 'bg-emerald-500' : 'bg-slate-200';

  return (
    <div className="h-full overflow-auto pr-1">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white">
          <tr className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            <th className="py-1 text-left">Producto</th>
            {showPos && <th className="py-1 text-left">Tienda</th>}
            {showStock && <th className="py-1 text-right">Stock</th>}
            {showForecast && <th className="py-1 text-right">Pronóst.</th>}
            {showMin && <th className="py-1 text-right">Mín</th>}
            {showMax && <th className="py-1 text-right">Máx</th>}
            {showDays && <th className="py-1 text-right">Días</th>}
            {showUnits && <th className="py-1 text-right">Pedir</th>}
            {showFrom && <th className="py-1 text-left">Desde</th>}
            {showSupplier && <th className="py-1 text-left">Proveedor</th>}
            {showEstCost && <th className="py-1 text-right">Costo est.</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
              <td className="py-1.5">
                <div className="flex items-center gap-1.5">
                  {r.status && <span className={`h-2 w-2 shrink-0 rounded-full ${dot(r.status)}`} />}
                  <span className="truncate text-slate-700">{r.name}</span>
                </div>
                {r.sku && <span className="text-[10px] text-slate-400">{r.sku}</span>}
              </td>
              {showPos && <td className="py-1.5 text-slate-500">{r.pos ?? '—'}</td>}
              {showStock && (
                <td className={`py-1.5 text-right tabular-nums font-semibold ${(r.stock ?? 0) <= 0 ? 'text-red-600' : 'text-slate-700'}`}>
                  {Math.round(r.stock ?? 0)}
                </td>
              )}
              {showForecast && (
                <td className={`py-1.5 text-right tabular-nums ${(r.forecast ?? 0) < 0 ? 'text-red-500' : 'text-slate-500'}`}>
                  {Math.round(r.forecast ?? 0)}
                </td>
              )}
              {showMin && <td className="py-1.5 text-right tabular-nums text-slate-400">{Math.round(r.min ?? 0)}</td>}
              {showMax && <td className="py-1.5 text-right tabular-nums text-slate-400">{Math.round(r.max ?? 0)}</td>}
              {showDays && (
                <td className={`py-1.5 text-right tabular-nums font-semibold ${(r.days ?? 0) < 7 ? 'text-red-600' : (r.days ?? 0) < 14 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {r.days != null && r.days >= 999 ? '∞' : Math.round(r.days ?? 0)}
                </td>
              )}
              {showUnits && <td className="py-1.5 text-right tabular-nums font-bold text-blue-600">{Math.round(r.units ?? 0)}</td>}
              {showFrom && <td className="py-1.5 truncate text-slate-500">{r.from ?? '—'}</td>}
              {showSupplier && (
                <td className="py-1.5 truncate text-slate-500">
                  {r.supplier || '—'}
                  {r.supplierPrice != null && r.supplierPrice > 0 && (
                    <span className="ml-1 text-[10px] text-slate-400">${Math.round(r.supplierPrice).toLocaleString('es-MX')}</span>
                  )}
                </td>
              )}
              {showEstCost && (
                <td className="py-1.5 text-right tabular-nums font-semibold text-slate-700">
                  {r.estCost != null && r.estCost > 0 ? `$${Math.round(r.estCost).toLocaleString('es-MX')}` : '—'}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
